import { Flex, FormControl, FormErrorMessage } from "@chakra-ui/react";
import Input from "../inputs/Input";
import { FormProvider, useForm } from "react-hook-form";
import { forwardRef, useImperativeHandle } from "react";
import _ from "lodash";
import { type Ticket, TicketPriority } from "../../types/ticket.types";
import Select from "../inputs/Select";
import FileInput from "../FileInput";
import { useTicketLabels } from "../../hooks/ticket_labels.hooks";
import { cn } from "../../lib/utils";

type CreateTicketFormProps = {
	defaultValue?: Partial<Ticket>;
	editableDefault?: boolean;
};

export type CreateTicketFormHandler = {
	valid: () => Promise<boolean>;
	values: () => Ticket;
	isDirty: () => boolean;
};

const CreateTicketForm = forwardRef<
	CreateTicketFormHandler,
	CreateTicketFormProps
>(({ defaultValue, editableDefault }, ref) => {
	const methods = useForm();
	editableDefault = editableDefault ?? true;
	useImperativeHandle(ref, () => ({
		valid: () => {
			return methods.trigger();
		},
		values: () => {
			return methods.getValues() as Ticket;
		},
		isDirty: () => {
			return !_.isEqual(methods.getValues(), {
				ticketLabelId: "",
				commandId: "",
				priority: TicketPriority.LOW,
				description: "",
				files: [],
			});
		},
	}));
	const { data: labels } = useTicketLabels({});
	const labelId = methods.watch("ticketLabelId");
	const label = labels?.find(
		(label) => label.id.toString() === labelId.toString(),
	);

	return (
		<Flex w="100%">
			<FormProvider {...methods}>
				<form style={{ marginTop: "32px", width: "100%" }}>
					<Flex direction="column" gap="24px">
						<FormControl isInvalid={!!methods.formState.errors.ticketLabelId}>
							<Select
								id="ticketLabelId"
								placeholder="Motif"
								{...methods.register("ticketLabelId", {
									required: "Vous devez sélectionner un motif.",
								})}
								isDisabled={
									editableDefault === false &&
									defaultValue?.ticketLabelId != null
								}
							>
								{labels?.map((label) => (
									<option key={label.id} value={label.id}>
										{label.label}
									</option>
								))}
							</Select>
							<FormErrorMessage>
								<>{methods.formState.errors?.ticketLabelId?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.commandId}>
							<Input
								id={"commandId"}
								placeholder="Numéro de commande"
								isDisabled={
									editableDefault === false && defaultValue?.commandId != null
								}
								defaultValue={defaultValue?.commandId}
								{...methods.register("commandId")}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.commandId?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.description}>
							<textarea
								id={"description"}
								placeholder="Message"
								className={cn(
									"w-full h-80",
									"border! border-gray-400 rounded-xl",
									"p-4!",
								)}
								aria-multiline={true}
								disabled={
									editableDefault === false && defaultValue?.description != null
								}
								{...methods.register("description")}
							></textarea>
							<FormErrorMessage>
								<>{methods.formState.errors?.description?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.files}>
							<div className="text-gray-500 text-sm mb-2">
								{label?.proof === true
									? "Preuve (Obligatoire)"
									: "Preuve (Facultative)"}
							</div>
							<FileInput
								state={
									methods.formState.errors.files === undefined
										? "default"
										: "error"
								}
								mandatory={label?.proof === true}
								onFileChange={(files) => {
									methods.setValue("files", files);
								}}
								{...methods.register("files", {
									validate: (value) => {
										if (label?.proof === true && value.length === 0) {
											return "Le fichier est obligatoire";
										}
										return true;
									},
								})}
								onChange={() => {}}
								onBlur={() => {}}
							></FileInput>
							<FormErrorMessage>
								<>{methods.formState.errors?.files?.message}</>
							</FormErrorMessage>
						</FormControl>
					</Flex>
				</form>
			</FormProvider>
		</Flex>
	);
});

export default CreateTicketForm;
